import React, {
  useRef,
  useLayoutEffect,
  useState,
  useCallback,
  useEffect,
  forwardRef,
  cloneElement,
  Ref,
  FunctionComponent,
  memo,
} from "react";
import { PopoverPortal } from "./PopoverPortal";
import {
  ContentLocation,
  ContentLocationGetter,
  PopoverPosition,
  PopoverProps,
  PopoverState,
  RenderPopoverProps,
} from "./types";
import { EMPTY_CLIENT_RECT, rectsAreEqual } from "./util";
import { usePopover } from "./usePopover";
import { useMemoizedArray } from "./useMemoizedArray";

const DEFAULT_POSITIONS: PopoverPosition[] = ["top", "left", "right", "bottom"];

const PopoverInternal = forwardRef(
  (
    {
      isOpen,
      children,
      content,
      positions = DEFAULT_POSITIONS,
      align = "center",
      padding = 0,
      reposition = true,
      parentElement = window.document.body,
      boundaryElement = parentElement,
      containerClassName = "react-tiny-popover-container",
      containerStyle,
      contentLocation,
      boundaryInset = 0,
      onClickOutside,
      clickOutsideCapture = false,
    }: PopoverProps,
    externalRef: Ref<HTMLElement>
  ) => {
    const childRef = useRef<HTMLElement | undefined>();

    const handleRef = useCallback(
      (node: HTMLElement) => {
        childRef.current = node;
        if (externalRef != null) {
          if (typeof externalRef === "object") {
            (externalRef as React.MutableRefObject<HTMLElement>).current = node;
          } else if (typeof externalRef === "function") {
            (externalRef as (instance: HTMLElement) => void)(node);
          }
        }
      },
      [externalRef]
    );

    const renderChild = (): React.FunctionComponentElement<HTMLElement> =>
      cloneElement(children, {
        ref: handleRef,
      });

    return (
      <>
        {renderChild()}
        {isOpen && (
          <RenderPopover
            childRef={childRef}
            content={content}
            positions={positions}
            align={align}
            padding={padding}
            reposition={reposition}
            parentElement={parentElement}
            boundaryElement={boundaryElement}
            containerClassName={containerClassName}
            containerStyle={containerStyle}
            contentLocation={contentLocation}
            boundaryInset={boundaryInset}
            onClickOutside={onClickOutside}
            clickOutsideCapture={clickOutsideCapture}
          />
        )}
      </>
    );
  }
);

const RenderPopoverRaw: FunctionComponent<RenderPopoverProps> = ({
  childRef,
  content,
  positions: externalPositions,
  align,
  padding,
  reposition,
  parentElement,
  boundaryElement,
  containerClassName,
  containerStyle,
  contentLocation,
  boundaryInset,
  onClickOutside,
  clickOutsideCapture,
}): JSX.Element => {
  const positions = useMemoizedArray(externalPositions);

  // TODO: factor prevs out into a custom prevs hook
  const prevPositions = useRef<PopoverPosition[] | undefined>();
  const prevContentLocation = useRef<ContentLocation | ContentLocationGetter | undefined>();
  const prevReposition = useRef(reposition);

  const [popoverState, setPopoverState] = useState<PopoverState>({
    align,
    nudgedLeft: 0,
    nudgedTop: 0,
    position: positions[0],
    padding,
    childRect: EMPTY_CLIENT_RECT,
    popoverRect: EMPTY_CLIENT_RECT,
    parentRect: EMPTY_CLIENT_RECT,
    boundaryRect: EMPTY_CLIENT_RECT,
    boundaryInset,
    violations: EMPTY_CLIENT_RECT,
    hasViolations: false,
  });

  const { positionPopover, popoverRef, scoutRef } = usePopover({
    childRef,
    containerClassName,
    parentElement,
    boundaryElement,
    contentLocation,
    positions,
    align,
    padding,
    boundaryInset,
    reposition,
    onPositionPopover: setPopoverState,
  });

  useLayoutEffect(() => {
    let shouldUpdate = true;
    let request: number;

    const updatePopover = (): void => {
      if (shouldUpdate) {
        const childRect = childRef?.current?.getBoundingClientRect();
        const popoverRect = popoverRef?.current?.getBoundingClientRect();
        if (
          childRect != null &&
          popoverRect != null &&
          (!rectsAreEqual(childRect, {
            top: popoverState.childRect.top,
            left: popoverState.childRect.left,
            width: popoverState.childRect.width,
            height: popoverState.childRect.height,
            bottom: popoverState.childRect.top + popoverState.childRect.height,
            right: popoverState.childRect.left + popoverState.childRect.width,
          } as ClientRect) ||
            popoverRect.width !== popoverState.popoverRect.width ||
            popoverRect.height !== popoverState.popoverRect.height ||
            popoverState.padding !== padding ||
            popoverState.align !== align ||
            positions !== prevPositions.current ||
            contentLocation !== prevContentLocation.current ||
            reposition !== prevReposition.current)
        ) {
          positionPopover();
        }

        // TODO: factor prev checks out into the custom prevs hook
        if (positions !== prevPositions.current) {
          prevPositions.current = positions;
        }
        if (contentLocation !== prevContentLocation.current) {
          prevContentLocation.current = contentLocation;
        }
        if (reposition !== prevReposition.current) {
          prevReposition.current = reposition;
        }

        if (shouldUpdate) {
          request = window.requestAnimationFrame(updatePopover);
        }
      }
    };

    request = window.requestAnimationFrame(updatePopover);

    return () => {
      shouldUpdate = false;
      cancelAnimationFrame(request);
    };
  }, [
    align,
    childRef,
    contentLocation,
    padding,
    popoverRef,
    popoverState.align,
    popoverState.childRect.height,
    popoverState.childRect.left,
    popoverState.childRect.top,
    popoverState.childRect.width,
    popoverState.padding,
    popoverState.popoverRect.height,
    popoverState.popoverRect.width,
    positionPopover,
    positions,
    reposition,
  ]);

  useEffect(() => {
    const popoverElement = popoverRef.current;

    Object.assign(popoverElement.style, containerStyle);

    return () => {
      Object.keys(containerStyle ?? {}).forEach(
        (key) =>
          delete popoverElement.style[
            key as keyof Omit<typeof containerStyle, "length" | "parentRule">
          ]
      );
    };
  }, [containerStyle, popoverRef]);

  const handleOnClickOutside = useCallback(
    (e: MouseEvent) => {
      if (
        !popoverRef.current?.contains(e.target as Node) &&
        !childRef.current?.contains(e.target as Node)
      ) {
        onClickOutside?.(e);
      }
    },
    [childRef, onClickOutside, popoverRef]
  );

  useEffect(() => {
    let request: number;

    const handleWindowResize = (): void => {
      if (childRef.current) {
        request = window.requestAnimationFrame(() => positionPopover());
      }
    };

    window.addEventListener("click", handleOnClickOutside, clickOutsideCapture);
    window.addEventListener("contextmenu", handleOnClickOutside, clickOutsideCapture);
    window.addEventListener("resize", handleWindowResize);

    return () => {
      cancelAnimationFrame(request);
      window.removeEventListener("click", handleOnClickOutside, clickOutsideCapture);
      window.removeEventListener("contextmenu", handleOnClickOutside, clickOutsideCapture);
      window.removeEventListener("resize", handleWindowResize);
    };
  }, [childRef, clickOutsideCapture, handleOnClickOutside, positionPopover]);

  return (
    <PopoverPortal
      element={popoverRef.current}
      scoutElement={scoutRef.current}
      container={parentElement}
    >
      {typeof content === "function" ? content(popoverState) : content}
    </PopoverPortal>
  );
};

const RenderPopover = memo(RenderPopoverRaw);

export const Popover = forwardRef<HTMLElement, PopoverProps>((props, ref) => {
  if (typeof window === "undefined") return props.children;
  return <PopoverInternal {...props} ref={ref} />;
});

PopoverInternal.displayName = "PopoverInternal";
Popover.displayName = "Popover";
